import { initContract } from "@ts-rest/core";
import { z } from "zod";

import { NestingRunSchema } from "../generated/prisma/zod";

const c = initContract();

export const NestingRunLayoutViewSchema = z.object({
  index: z.number(),
  count: z.number().describe("Number of identical sheets cut with this layout"),
  sheetIndices: z.array(z.number()),
  widthMm: z.number(),
  heightMm: z.number(),
  pieceCount: z.number(),
  utilization: z.number(),
  svgUrl: z.string().describe("Signed URL of the layout SVG, valid for one hour"),
});

export const NestingRunItemViewSchema = z.object({
  itemId: z.string().describe("Id of the quote request line nested as this item"),
  netAreaMm2: z.number().describe("Area of one piece, in mm²"),
  allocatedAreaMm2: z
    .number()
    .describe("Share of the consumed sheet area allocated to one piece, scrap included, in mm²"),
});

export const NestingRunViewSchema = NestingRunSchema.pick({
  id: true,
  layoutId: true,
  status: true,
  isFallback: true,
  sheetsUsed: true,
  consumedAreaMm2: true,
  warnings: true,
  errorCode: true,
  errorItemId: true,
  createdAt: true,
}).extend({
  layouts: z.array(NestingRunLayoutViewSchema),
  items: z.array(NestingRunItemViewSchema),
  boundingBoxFlatPatternItemIds: z
    .array(z.string())
    .describe("Ids of the items nested as the bounding rectangle of a 2D drawing"),
});

export const nestingContract = c.router({
  runLayoutNesting: {
    method: "POST",
    path: "/layouts/:id/nesting",
    body: c.noBody(),
    responses: { 201: NestingRunViewSchema },
    summary: "Start a true-shape nesting of the layout sheet metal lines",
  },
  getLayoutNesting: {
    method: "GET",
    path: "/layouts/:id/nesting",
    responses: { 200: NestingRunViewSchema.nullable() },
    summary: "Latest nesting run of the layout",
  },
  runOptimizationNesting: {
    method: "POST",
    path: "/optimizations/:id/nesting",
    body: z.object({
      cutWidth: z.number().min(0).optional(),
      skeletonWidth: z.number().min(0).optional(),
      newScrapMaterialBillingRate: z.number().min(0).max(100).optional(),
    }),
    responses: { 201: NestingRunViewSchema },
    summary: "Start a true-shape nesting of all the lines of a sheet nesting optimization",
  },
  applyLayoutNesting: {
    method: "POST",
    path: "/layouts/:id/nesting/apply",
    body: c.noBody(),
    responses: { 200: NestingRunViewSchema },
    summary: "Apply the latest nesting run of the layout to its material layout",
  },
});

export type NestingRunView = z.infer<typeof NestingRunViewSchema>;
export type NestingRunItemView = z.infer<typeof NestingRunItemViewSchema>;
export type NestingRunLayoutView = z.infer<typeof NestingRunLayoutViewSchema>;
